import type { PreferencesDto } from '@dbrain/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { api } from './api/client.js';
import { keys, usePreferences } from './api/hooks.js';

/**
 * The user's time zone (Phase 11 open question 1): the browser's zone is
 * sent to the server whenever it differs from the stored one, so the
 * assistant sees the user's local date and time; a zone chosen in the
 * settings wins over it, and the organization's default covers the rest.
 */
export function browserTimeZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

/** Every zone the browser knows, for the settings' list. */
export function timeZones(): string[] {
  try {
    return (Intl as unknown as { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf?.('timeZone') ?? [];
  } catch {
    return [];
  }
}

export function useDetectTimeZone(enabled: boolean): void {
  const queryClient = useQueryClient();
  const preferences = usePreferences();
  const stored = preferences.data?.detectedTimeZone;
  const loaded = enabled && preferences.isSuccess;
  useEffect(() => {
    const zone = browserTimeZone();
    if (!loaded || !zone || zone === stored) return;
    void api<PreferencesDto>('PATCH', '/api/me/preferences', { detectedTimeZone: zone })
      .then((data) => queryClient.setQueryData(keys.preferences, data))
      .catch(() => undefined);
  }, [loaded, stored, queryClient]);
}
